import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Tabs, Tab, Box, Typography, Avatar, Chip,
    CircularProgress, Stack, Menu, MenuItem, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, TextField, Alert,
    InputAdornment, IconButton, Paper, Tooltip
} from '@mui/material';
import PeopleIcon              from '@mui/icons-material/People';
import GroupsIcon              from '@mui/icons-material/Groups';
import AdminPanelSettingsIcon  from '@mui/icons-material/AdminPanelSettings';
import Visibility              from '@mui/icons-material/Visibility';
import VisibilityOff           from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon           from '@mui/icons-material/OpenInNew';
import DeleteForeverIcon       from '@mui/icons-material/DeleteForever';
import { useNavigate }         from 'react-router-dom';
import { useTranslation }      from 'react-i18next';
import axios                   from '../api/axiosConfig';
import { useAuth }             from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const CRIMSON    = '#8B2020';

const ALL_TABS = [
    { key: 'customers', role: 'CLIENT',   icon: <PeopleIcon /> },
    { key: 'guides',    role: 'GUIDE',    icon: <GroupsIcon /> },
    { key: 'managers',  role: 'MANAGER',  icon: <AdminPanelSettingsIcon /> },
];

const roleStyle = (role) => {
    if (role === 'ADMIN')   return { bgcolor: CRIMSON,    color: 'white' };
    if (role === 'MANAGER') return { bgcolor: GOLD,       color: WALNUT };
    if (role === 'GUIDE')   return { bgcolor: FOREST,     color: 'white' };
    return                         { bgcolor: TERRACOTTA, color: 'white' };
};

export default function UserManagement() {
    const { user: manager } = useAuth();
    const navigate          = useNavigate();
    const { t }             = useTranslation();

    const isAdmin   = manager?.role === 'ADMIN';
    const isManager = manager?.role === 'MANAGER';

    // ADMIN sees all tabs; MANAGER sees only customers and guides
    const TABS = isAdmin ? ALL_TABS : ALL_TABS.filter(t => t.key !== 'managers');

    // ADMIN can assign any role; MANAGER can only toggle between CLIENT and GUIDE
    const ROLE_OPTIONS = isAdmin
        ? ['ADMIN', 'MANAGER', 'GUIDE', 'CLIENT']
        : ['GUIDE', 'CLIENT'];

    const [tab,     setTab]     = useState(0);
    const [users,   setUsers]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [countries, setCountries] = useState({});   // { [userId]: country }

    // Role-change menu
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuUser,   setMenuUser]   = useState(null);

    // Pending change (waiting for password confirmation)
    const [pending, setPending] = useState(null);     // { userId, newRole, username }

    // Password dialog (role change)
    const [pwdOpen,    setPwdOpen]    = useState(false);
    const [password,   setPassword]   = useState('');
    const [showPwd,    setShowPwd]    = useState(false);
    const [pwdError,   setPwdError]   = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);

    // Delete dialog
    const [deleteTarget,  setDeleteTarget]  = useState(null);  // { id, username }
    const [deletePwdOpen, setDeletePwdOpen] = useState(false);
    const [deletePwd,     setDeletePwd]     = useState('');
    const [deletePwdShow, setDeletePwdShow] = useState(false);
    const [deleteError,   setDeleteError]   = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Guard — only MANAGER and ADMIN can access this page
    useEffect(() => {
        if (manager && !isAdmin && !isManager) navigate('/');
    }, [manager, isAdmin, isManager, navigate]);

    // Fetch all users once
    useEffect(() => {
        axios.get('/api/app_user/view?size=1000')
            .then(res => setUsers(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
            .catch(() => setUsers([]))
            .finally(() => setLoading(false));
    }, []);

    const currentRole = TABS[tab]?.role;
    const filtered    = useMemo(
        () => users.filter(u => u.role === currentRole),
        [users, currentRole]
    );

    // Lazily fetch country for CLIENT users
    useEffect(() => {
        if (currentRole !== 'CLIENT') return;
        filtered.forEach(u => {
            if (countries[u.id] !== undefined) return;
            axios.get(`/api/clients/view/id/${u.id}`)
                .then(res => setCountries(prev => ({ ...prev, [u.id]: res.data.country || '—' })))
                .catch(()  => setCountries(prev => ({ ...prev, [u.id]: '—' })));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRole, filtered.length]);

    // ── Role menu ─────────────────────────────────────────────────────────────
    const canOpenMenu = (u) => {
        // MANAGER cannot change their own role
        if (isManager && u.id === manager?.id) return false;
        return true;
    };

    const openMenu = (e, u) => {
        if (!canOpenMenu(u)) return;
        setMenuAnchor(e.currentTarget);
        setMenuUser(u);
    };
    const closeMenu = () => { setMenuAnchor(null); setMenuUser(null); };

    const selectRole = (newRole) => {
        if (newRole === menuUser.role) { closeMenu(); return; }
        setPending({ userId: menuUser.id, newRole, username: menuUser.username });
        closeMenu();
        setPwdOpen(true);
    };

    // ── Password dialog ───────────────────────────────────────────────────────
    const closePwdDialog = () => {
        setPwdOpen(false);
        setPassword('');
        setPwdError('');
        setShowPwd(false);
        setPending(null);
    };

    const confirmChange = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setPwdLoading(true);
        setPwdError('');
        try {
            // Step 1: verify the manager's own password
            try {
                await axios.post('/api/app_user/login', { email: manager.email, password: password.trim() });
            } catch (loginErr) {
                const status = loginErr.response?.status;
                const code   = loginErr.response?.data?.error;
                setPwdError(
                    status === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                        ? t('users.wrong_password')
                        : t('users.change_failed')
                );
                return;
            }

            // Step 2: perform role conversion
            try {
                await axios.post(`/api/app_user/convert-role/${pending.userId}`, { newRole: pending.newRole });
            } catch {
                setPwdError(t('users.change_failed'));
                return;
            }

            setUsers(prev =>
                prev.map(u => u.id === pending.userId ? { ...u, role: pending.newRole } : u)
            );
            closePwdDialog();
        } finally {
            setPwdLoading(false);
        }
    };

    // ── Delete helpers ────────────────────────────────────────────────────────
    const canDelete = (u) => {
        if (u.role === 'ADMIN') return false;
        if (isManager && (u.role === 'CLIENT' || u.role === 'GUIDE')) return true;
        if (isAdmin   && (u.role === 'CLIENT' || u.role === 'GUIDE' || u.role === 'MANAGER')) return true;
        return false;
    };

    const openDelete = (u) => {
        setDeleteTarget({ id: u.id, username: u.username });
        setDeletePwd('');
        setDeleteError('');
        setDeletePwdShow(false);
        setDeletePwdOpen(true);
    };

    const closeDelete = () => {
        setDeletePwdOpen(false);
        setDeleteTarget(null);
        setDeletePwd('');
        setDeleteError('');
    };

    const confirmDelete = async () => {
        if (!deletePwd) { setDeleteError(t('users.password_required')); return; }
        setDeleteLoading(true);
        setDeleteError('');
        try {
            // Step 1: verify password
            try {
                await axios.post('/api/app_user/login', { email: manager.email, password: deletePwd.trim() });
            } catch (loginErr) {
                const st   = loginErr.response?.status;
                const code = loginErr.response?.data?.error;
                setDeleteError(
                    st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                        ? t('users.wrong_password')
                        : t('users.delete_failed', 'Failed to delete user.')
                );
                return;
            }

            // Step 2: delete user
            try {
                await axios.delete(`/api/app_user/${deleteTarget.id}`);
            } catch (delErr) {
                const code = delErr.response?.data?.error;
                const st   = delErr.response?.status;
                setDeleteError(
                    st === 409
                        ? (code || t('users.delete_has_data', 'Cannot delete: user has booking history. Deactivate instead.'))
                        : t('users.delete_failed', 'Failed to delete user.')
                );
                return;
            }

            setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
            closeDelete();
        } finally {
            setDeleteLoading(false);
        }
    };

    if (!manager || (!isAdmin && !isManager)) return null;

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://machanents.com/media/product/RGSN0329-1.jpg") center/cover fixed` }}>

            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${TERRACOTTA} 100%)`,
                height: 120, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' }
            }} />

            <Container maxWidth="lg" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="overline"
                        sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                        {t('users.overline')}
                    </Typography>
                    <Typography variant="h4" fontWeight={900}
                        sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                        {t('users.title')}
                    </Typography>
                </Box>

                {/* Tabs */}
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    sx={{
                        mb: 3,
                        borderBottom: '2px solid rgba(196,98,45,0.15)',
                        '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', color: 'text.secondary' },
                        '& .Mui-selected': { color: TERRACOTTA },
                        '& .MuiTabs-indicator': { bgcolor: TERRACOTTA },
                    }}>
                    {TABS.map((item, i) => (
                        <Tab key={i}
                            label={`${t(`users.tab_${item.key}`)} (${users.filter(u => u.role === item.role).length})`}
                        />
                    ))}
                </Tabs>

                {/* Content */}
                {loading ? (
                    <Box display="flex" justifyContent="center" py={8}>
                        <CircularProgress sx={{ color: TERRACOTTA }} />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Typography color="text.secondary">{t('users.no_users')}</Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {filtered.map(u => {
                            const rs       = roleStyle(u.role);
                            const clickable = canOpenMenu(u);
                            return (
                                <Paper key={u.id} elevation={0} sx={{
                                    display: 'flex', alignItems: 'center', gap: 2.5,
                                    p: 2, borderRadius: 3,
                                    border: '1px solid rgba(196,98,45,0.1)',
                                    bgcolor: 'white', transition: '0.2s',
                                    '&:hover': {
                                        boxShadow: '0 4px 16px rgba(44,31,20,0.08)',
                                        borderColor: 'rgba(196,98,45,0.2)',
                                    },
                                }}>
                                    {/* Avatar */}
                                    <Avatar src={u.imageUrl} alt={u.username}
                                        sx={{ width: 50, height: 50, bgcolor: TERRACOTTA,
                                            border: '2px solid rgba(196,98,45,0.2)', flexShrink: 0 }}>
                                        {u.username?.[0]?.toUpperCase()}
                                    </Avatar>

                                    {/* User info */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography fontWeight={700} sx={{ color: WALNUT, lineHeight: 1.2 }}>
                                            {u.username}
                                        </Typography>
                                        <Stack direction="row" spacing={2} mt={0.3} flexWrap="wrap" useFlexGap>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {u.email}
                                            </Typography>
                                            {u.phone && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {u.phone}
                                                </Typography>
                                            )}
                                            {currentRole === 'CLIENT' && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {countries[u.id] !== undefined ? countries[u.id] : '…'}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                            {t('users.joined')}{': '}
                                            {u.createdAt
                                                ? new Date(u.createdAt).toLocaleDateString('en-GB', {
                                                    day: 'numeric', month: 'short', year: 'numeric',
                                                })
                                                : '—'}
                                        </Typography>
                                    </Box>

                                    {/* View Profile — guides (MANAGER+ADMIN) and managers (ADMIN only) */}
                                    {u.role === 'MANAGER' && isAdmin && (
                                        <IconButton size="small" component="a" href={`/managers/${u.id}`}
                                            title={t('users.view_profile', 'View Profile')}
                                            sx={{ color: GOLD, border: `1px solid rgba(201,151,58,0.25)`,
                                                borderRadius: 1.5, flexShrink: 0 }}>
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {u.role === 'GUIDE' && (
                                        <IconButton size="small" component="a" href={`/guides/${u.id}`}
                                            title={t('users.view_profile', 'View Profile')}
                                            sx={{ color: FOREST, border: `1px solid rgba(74,103,65,0.25)`,
                                                borderRadius: 1.5, flexShrink: 0 }}>
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    )}

                                    {/* Delete button */}
                                    {canDelete(u) && (
                                        <IconButton size="small" onClick={() => openDelete(u)}
                                            title={t('users.delete_user', 'Delete User')}
                                            sx={{ color: '#8B2020', border: '1px solid rgba(139,32,32,0.25)',
                                                borderRadius: 1.5, flexShrink: 0 }}>
                                            <DeleteForeverIcon fontSize="small" />
                                        </IconButton>
                                    )}

                                    {/* Role badge — clickable only when allowed */}
                                    <Chip
                                        label={u.role === 'CLIENT' ? 'CUSTOMER' : u.role}
                                        onClick={clickable ? (e) => openMenu(e, u) : undefined}
                                        sx={{
                                            ...rs, fontWeight: 700, fontSize: '0.72rem',
                                            cursor: clickable ? 'pointer' : 'default',
                                            flexShrink: 0, transition: '0.15s',
                                            ...(clickable && {
                                                '&:hover': { opacity: 0.82, transform: 'scale(1.05)' },
                                            }),
                                        }}
                                    />
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Container>

            {/* ── Role dropdown menu ──────────────────────────────────────────────── */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
                PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(44,31,20,0.14)', minWidth: 160 } }}>
                {ROLE_OPTIONS.map(role => {
                    const rs = roleStyle(role);
                    const isActive = menuUser?.role === role;
                    return (
                        <MenuItem key={role} onClick={() => selectRole(role)}
                            sx={{
                                gap: 1.5, fontWeight: isActive ? 800 : 600, py: 1,
                                color: isActive ? rs.bgcolor : 'inherit',
                                '&:hover': { bgcolor: `${rs.bgcolor}18` },
                            }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%',
                                bgcolor: rs.bgcolor, flexShrink: 0 }} />
                            {role === 'CLIENT' ? 'CUSTOMER' : role}
                        </MenuItem>
                    );
                })}
            </Menu>

            {/* ── Delete user confirmation dialog ───────────────────────────────── */}
            <Dialog open={deletePwdOpen} onClose={closeDelete} fullWidth maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700}
                    sx={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#8B2020', pb: 1 }}>
                    {t('users.delete_title', 'Delete User')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        {t('users.delete_confirm_text',
                            'Permanently delete {{username}}? This cannot be undone. Enter your password to confirm.',
                            { username: deleteTarget?.username })}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('users.your_password')}
                        type={deletePwdShow ? 'text' : 'password'}
                        value={deletePwd}
                        onChange={e => { setDeletePwd(e.target.value); setDeleteError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmDelete(); }}
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setDeletePwdShow(p => !p)} edge="end">
                                        {deletePwdShow ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {deleteError && <Alert severity="error" sx={{ mt: 1.5 }}>{deleteError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeDelete} disabled={deleteLoading}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={confirmDelete} disabled={deleteLoading}
                        sx={{ bgcolor: '#8B2020', '&:hover': { bgcolor: '#6B1515' },
                            borderRadius: 2, fontWeight: 700 }}>
                        {deleteLoading ? t('common.saving') : t('users.delete_btn', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Password confirmation dialog ───────────────────────────────────── */}
            <Dialog open={pwdOpen} onClose={closePwdDialog} fullWidth maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700}
                    sx={{ fontFamily: "'Playfair Display', Georgia, serif", pb: 1 }}>
                    {t('users.confirm_title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        {t('users.confirm_text', {
                            username: pending?.username,
                            role: pending?.newRole === 'CLIENT' ? 'CUSTOMER' : pending?.newRole,
                        })}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('users.your_password')}
                        type={showPwd ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmChange(); }}
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                        {showPwd ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closePwdDialog} disabled={pwdLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" onClick={confirmChange} disabled={pwdLoading}
                        sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' },
                            borderRadius: 2, fontWeight: 700 }}>
                        {pwdLoading ? t('common.saving') : t('users.confirm_btn')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
